import * as moment from 'moment-timezone';

import { LOCAL_TZ, ZULU } from '../globals';


/**
 * Default moment.js formatting string to use for formatting the date portion of a date/time in Zulu format.
 */
export const ZULU_DATE_FMT = 'M/D';


/**
 * Default moment.js formatting string to use for formatting the time portion of a date/time in Zulu format.
 */
export const ZULU_TIME_FMT = 'HH:mm[Z]';


/**
 * Default moment.js formatting string to use for formatting a Zulu date and time.
 */
export const ZULU_DATETIME_FMT = ZULU_DATE_FMT + ' ' + ZULU_TIME_FMT;


/**
 * Default moment.js formatting string to use for formatting the date portion of a date/time in local format.
 */
export const LOCAL_DATE_FMT = 'MMM D';


/**
 * Default moment.js formatting string to use for formatting the time portion of a date/time in local format.
 */
export const LOCAL_TIME_FMT = 'h:mm A z';


/**
 * Default moment.js formatting string to use for formatting a local date and time.
 */
export const LOCAL_DATETIME_FMT = LOCAL_DATE_FMT + ' ' + LOCAL_TIME_FMT;


/**
 * Helper type for splitting a formatted date out into its component parts.
 */
export interface FormattedDateParts {
  /**
   * Day layel. Only populated if the date is Yesterday, Today, or Tomorrow (otherwise this will be null).
   */
  day: string;

  /**
   * Date portion of the date (e.g., "Sept 21" or "9/21")
   */
  date: string;

  /**
   * Time portion of the date, including AM/PM for timezones other than Zulu (e.g., "11:00 AM" or "15:00Z").
   */
  time: string;

  /**
   * Timezone label (e.g., "EDT"). Only specified for timezones other than UTC/Zulu (Zulu times are always printed with
   * the "Z" suffix on the time itself, rather than specifying the timezone separately).
   */
  tz: string;
}


/**
 * Options for formatting a date/time range
 */
export interface RangeFormatOptions {
  /**
   * Timezone to use for formatting dates
   */
  tz?: string;

  /**
   * If true, "Yesterday", "Today", and "Tomorrow" will be used instead of the full date (e.g., "9/21) if the date
   * is within a day of today.
   */
  relative?: boolean;
}


/**
 * Defaults to use for formatting dates and times.
 */
export const DEFAULT_RANGE_FORMAT_OPTIONS: RangeFormatOptions = {
  tz: LOCAL_TZ,
  relative: true
};


/**
 * Formats the time range as a short, user-friendly string that avoids repeating redundant elements (e.g., the date
 * portion of the date/time is only printed once if it does not change between the start date and end date).
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {RangeFormatOptions} [options] Options to use for formatting the dates and times in the range.
 * @returns {string}
 */
export function formatTimeRange(startDate: Date, endDate: Date, options: RangeFormatOptions = null): string {
  let result = '';
  options = Object.assign({}, DEFAULT_RANGE_FORMAT_OPTIONS, options);
  const tz = options.tz;
  const zulu = tz === ZULU;
  const m = {
    start: moment(startDate).tz(tz),
    end: moment(endDate).tz(tz)
  };
  const f = {
    start: getFormattedDate(m.start, tz),
    end: getFormattedDate(m.end, tz)
  };

  // Print start date
  result += options.relative && !zulu && f.start.day ? f.start.day : f.start.date;

  // Print start time
  result += ' ' + f.start.time;

  // Print separator
  result += ' - ';

  // Print end date (if different from start date)
  if (!isSameDay(m.start, m.end, tz)) {
    result += options.relative && !zulu && f.end.day ? f.end.day : f.end.date;
    result += ' ';
  }

  // Print end time
  result += f.end.time;

  // Print timezone suffix (except for Zulu time, since the "Z" suffix would already be on both the start and end
  // times).
  if (!zulu) {
    result += ' ' + f.end.tz;
  }

  return result;
}


/**
 * Returns an object with formatted parts of a moment object representing a date and time
 * @param {moment} m Moment.js instance
 * @param {string} tz Timezone to use for formatting
 * @returns {FormattedDateParts} Formatted parts of the date and time
 */
export function getFormattedDate(m: moment, tz: string): FormattedDateParts {
  return {
    day: formatDay(m, tz),
    date: formatDate(m, tz),
    time: formatTime(m, tz),
    tz: formatTimezone(tz)
  };
}


/**
 * Returns true if the given moment instance represents a date that was yesterday (taking the timezone into
 * consideration)
 * @param {moment} m Moment instance
 * @param {string} tz Timezone
 * @returns {boolean}
 */
export function isYesterday(m: moment, tz: string): boolean {
  const yesterday = moment().add(-1, 'day');
  return isSameDay(m, yesterday, tz);
}


/**
 * Returns true if the given moment instance is today (taking the timezone into consideration)
 * @param {moment} m Moment instance
 * @param {string} tz Timezone
 * @returns {boolean}
 */
export function isToday(m: moment, tz: string): boolean {
  const now = moment();
  return isSameDay(m, now, tz);
}


/**
 * Returns true if the given moment instance represents a date that is tomorrow (taking the timezone into
 * consideration)
 * @param {moment} m Moment instance
 * @param {string} tz Timezone
 * @returns {boolean}
 */
export function isTomorrow(m: moment, tz: string): boolean {
  const yesterday = moment().add(1, 'day');
  return isSameDay(m, yesterday, tz);
}


/**
 * Returns true if the two moment instances fall on the same day, taking timezone into account.
 * @param {moment} m1 First moment instance
 * @param {moment} m2 Second moment instance
 * @param {string} tz Timezone
 * @returns {boolean}
 */
export function isSameDay(m1: moment, m2: moment, tz: string): boolean {
  return m1.tz(tz).format('M/D/Y') === m2.tz(tz).format('M/D/Y');
}


/**
 * Formats the day label ("Yesterday", "Today", or "Tomorrow") for a moment instance. If the day is earlier than
 * yesterday, or after tomorrow, this returns null.
 * @param {moment} m Moment instance
 * @param {string} tz Timezone
 * @returns {string}
 */
export function formatDay(m: moment, tz: string): string {
  if (isYesterday(m, tz)) {
    return 'Yesterday';
  }
  if (isToday(m, tz)) {
    return 'Today';
  }
  if (isTomorrow(m, tz)) {
    return 'Tomorrow';
  }
  return null;
}


/**
 * Formats the date portion of a moment instance using the default formatting options. For timezones other than Zulu,
 * the month name will be spelled out in abbreviated form (e.g., "Sept 21"); for Zulu, the month and date will use the
 * more condensed, numeric format (e.g., "9/21").
 * @param {moment} m
 * @param {string} tz
 * @returns {string}
 */
export function formatDate(m: moment, tz: string): string {
  return m.tz(tz).format(tz === ZULU ? ZULU_DATE_FMT : LOCAL_DATE_FMT);
}

/**
 * Formats the date portion of a moment instance using the default formatting options. For timezones other than Zulu,
 * the month name will be spelled out in abbreviated form (e.g., "Sept 21"); for Zulu, the month and date will use the
 * more condensed, numeric format (e.g., "9/21").
 * @param {moment} m
 * @param {string} tz
 * @returns {string}
 */
export function formatFullDate(m: moment, tz: string): string {
  return m.tz(tz).format('ll');
}

/**
 * Formats the time portion of a moment instance using the default formatting options. For timezones other than Zulu,
 * this will generally be the time using the 12-hour clock, without the AM/PM (e.g., "12:45"). For Zulu, this will be
 * the time on a 24-hour clock with a "Z" suffix (e.g., "16:00Z").
 * @param {moment} m Moment instance
 * @param {string} tz Timezone
 * @returns {string}
 */
export function formatTime(m: moment, tz: string): string {
  return m.tz(tz).format(tz === ZULU ? ZULU_TIME_FMT : 'h:mm A');
}


/**
 * Formats the timezone suffix (e.g., "EDT"). For Zulu times, this returns an empty string.
 * @param {string} tz Timezone
 * @returns {string} Timezone suffix
 */
export function formatTimezone(tz: string): string {
  return tz === ZULU ? '' : moment.tz(tz).format('z');
}
